import { SearchOutlined } from "@mui/icons-material";
import { InputAdornment, TextField } from "@mui/material";
import { useEffect, useMemo, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { fetchHelpTopicManifest } from "@ryde/layout";
import {
  companyIdSelector,
  formatHelpTopicLabel,
  type IHelpTopicManifest,
  type IHelpTopicManifestSection,
  type IHelpTopicManifestTopic,
} from "@ryde/shared";

import {
  buildHelpTopicSearchHaystack,
  matchesHelpTopicSearch,
} from "./help-topics-search.helpers";
import {
  HelpTopicsGroupTitleStyled,
  HelpTopicsNavLinkStyled,
  HelpTopicsSearchFieldWrapper,
  HelpTopicsSectionBlockStyled,
  HelpTopicsSectionTitleStyled,
  HelpTopicsSidebarEmptyStyled,
  HelpTopicsSidebarHeaderStyled,
  HelpTopicsSidebarScrollStyled,
  HelpTopicsSidebarStyled,
} from "./help-topics.page.styled";

interface IHelpTopicsSidebarProps {
  basePath: string;
  isMobileOpen?: boolean;
  onNavigate?: () => void;
}

interface IFilteredSection {
  section: IHelpTopicManifestSection;
  topics: IHelpTopicManifestTopic[];
}

const filterManifestSections = (
  manifest: IHelpTopicManifest | null,
  query: string,
  t: ReturnType<typeof useTranslation>["t"],
): IFilteredSection[] => {
  if (!manifest) {
    return [];
  }

  const sections: IFilteredSection[] = [];

  for (const group of manifest.groups) {
    for (const section of group.sections) {
      const topics = section.topics.filter(topic => {
        const haystack = buildHelpTopicSearchHaystack(topic.id, topic.label, t);
        return matchesHelpTopicSearch(haystack, query);
      });

      if (topics.length > 0) {
        sections.push({ section, topics });
      }
    }
  }

  return sections;
};

export const HelpTopicsSidebar: FC<IHelpTopicsSidebarProps> = ({
  basePath,
  isMobileOpen = false,
  onNavigate,
}) => {
  const { t } = useTranslation();
  const companyId = useRecoilValue(companyIdSelector);
  const [manifest, setManifest] = useState<IHelpTopicManifest | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!companyId) {
      return;
    }

    let cancelled = false;

    const loadManifest = async (): Promise<void> => {
      const result = await fetchHelpTopicManifest(companyId);
      if (!cancelled) {
        setManifest(
          result.success && result.data ? result.data : { groups: [] },
        );
      }
    };

    void loadManifest();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const filteredSections = useMemo(
    () => filterManifestSections(manifest, searchQuery, t),
    [manifest, searchQuery, t],
  );

  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <HelpTopicsSidebarStyled $isMobileOpen={isMobileOpen}>
      <HelpTopicsSidebarHeaderStyled>
        <h1>{t("helpTopics.title", { defaultValue: "Help topics" })}</h1>
        <p>
          {t("helpTopics.subtitle", {
            defaultValue: "Documentation for system settings and features.",
          })}
        </p>
        <HelpTopicsSearchFieldWrapper>
          <TextField
            size="small"
            fullWidth
            value={searchQuery}
            onChange={event => setSearchQuery(event.target.value)}
            placeholder={t("helpTopics.searchPlaceholder", {
              defaultValue: "Search parameters…",
            })}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </HelpTopicsSearchFieldWrapper>
      </HelpTopicsSidebarHeaderStyled>

      <HelpTopicsSidebarScrollStyled>
        {manifest?.groups.map(group => {
          const groupSections = filteredSections.filter(({ section }) =>
            group.sections.some(s => s.id === section.id),
          );

          if (groupSections.length === 0) {
            return null;
          }

          return (
            <div key={group.id}>
              <HelpTopicsGroupTitleStyled>
                {group.label}
              </HelpTopicsGroupTitleStyled>
              {groupSections.map(({ section, topics }) => (
                <HelpTopicsSectionBlockStyled key={section.id}>
                  <HelpTopicsSectionTitleStyled>
                    {section.label}
                  </HelpTopicsSectionTitleStyled>
                  {topics.map(topic => (
                    <HelpTopicsNavLinkStyled
                      key={topic.id}
                      to={`${basePath}/${section.routePrefix}/${topic.id}`}
                      end
                      onClick={onNavigate}
                    >
                      <span className="HelpTopicsNavLink-primary">
                        {t(`systemParam.formLabel.${topic.id}`, {
                          defaultValue:
                            topic.label || formatHelpTopicLabel(topic.id),
                        })}
                      </span>
                      <span className="HelpTopicsNavLink-secondary">
                        {topic.id}
                      </span>
                    </HelpTopicsNavLinkStyled>
                  ))}
                </HelpTopicsSectionBlockStyled>
              ))}
            </div>
          );
        })}

        {hasSearchQuery && filteredSections.length === 0 && (
          <HelpTopicsSidebarEmptyStyled role="status">
            {t("helpTopics.searchEmpty", {
              defaultValue: "No parameters match your search.",
            })}
          </HelpTopicsSidebarEmptyStyled>
        )}
      </HelpTopicsSidebarScrollStyled>
    </HelpTopicsSidebarStyled>
  );
};
